import API_CONFIG from '@/api/config'
import axios from 'axios'

/**
 * Axios client
 *
 * @type {AxiosInstance}
 */
const client = axios.create({})

let token = ''

// Add a request interceptor
client.interceptors.request.use(
    requestConfig => {
        requestConfig.headers.Accept = `application/json`
        if (API_CONFIG.WITH_CREDENTIALS && token) {
            requestConfig.headers.Authorization = `Bearer ${token}`
        }

        if (API_CONFIG.LOG_REQUESTS) {
            console.log('[Api Client Request] Payload: ', requestConfig)
        }

        return requestConfig
    },
    requestError => {
        if (API_CONFIG.LOG_REQUESTS) {
            console.error(`[Api Client Request Error]`, requestError)
        }

        return Promise.reject(requestError)
    }
)

// Add a response interceptor
client.interceptors.response.use(
    (response) => {
        return response
    },
    (error) => {
        return Promise.reject(error)
    }
)

/**
 * Parse query params
 *
 * @param params {object}
 * @returns {string}
 */
const parseParams = function (params) {
    let out = []
    for (let key in params) {
        if (!params.hasOwnProperty(key)) {
            break
        }

        if (Array.isArray(params[key])) {
            for (let i = 0; i < params[key].length; i++) {
                out.push(key + '[]=' + encodeURIComponent(params[key][i]))
            }
        } else {
            out.push(key + '=' + encodeURIComponent(params[key]))
        }
    }

    return out.length ? '?' + out.join('&') : ''
}

export default {
    /**
     *
     * @param url {string}
     * @param params {object}
     * @param conf {object}
     * @returns {Promise}
     */
    get (url, params = {}, conf = {}) {
        if (params) {
            url += parseParams(params)
        }

        return client
            .get(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error))
    },

    /**
     *
     * @param url {string}
     * @param params {object}
     * @param conf {object}
     * @returns {Promise}
     */
    delete (url, params = {}, conf = {}) {
        if (params) {
            url += parseParams(params)
        }

        return client
            .delete(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error))
    },

    /**
     *
     * @param url {string}
     * @param params {object}
     * @param conf {object}
     * @returns {Promise}
     */
    head (url, params = {}, conf = {}) {
        if (params) {
            url += parseParams(params)
        }

        return client
            .head(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error))
    },

    /**
     *
     * @param url {string}
     * @param params {object}
     * @param conf {object}
     * @returns {Promise}
     */
    options (url, params = {}, conf = {}) {
        if (params) {
            url += parseParams(params)
        }

        return client
            .options(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error))
    },

    /**
     *
     * @param url {string}
     * @param data {object}
     * @param params {object}
     * @param conf {object}
     * @returns {Promise}
     */
    post (url, data = {}, params = {}, conf = {}) {
        if (params) {
            url += parseParams(params)
        }

        return client
            .post(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error))
    },

    /**
     *
     * @param url {string}
     * @param data {object}
     * @param params {object}
     * @param conf {object}
     * @returns {Promise}
     */
    put (url, data = {}, params = {}, conf = {}) {
        if (params) {
            url += parseParams(params)
        }

        return client
            .put(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error))
    },

    /**
     *
     * @param url {string}
     * @param data {object}
     * @param params {object}
     * @param conf {object}
     * @returns {Promise}
     */
    patch (url, data = {}, params = {}, conf = {}) {
        if (params) {
            url += parseParams(params)
        }

        return client
            .patch(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error))
    },

    /**
     * Set token
     * @param {string} _token
     */
    token: {
        set (_token) {
            token = _token
        },
        clear () {
            token = ''
        }
    },
    /**
     * Axios client
     * @returns {AxiosInstance}
     */
    client () {
        return client
    }
}
